import {
  ClaimedDatabaseResource,
  CloudAccountApiListAbacAccountsRequest,
  DataAccessGroupInfo,
  DataGroupAccessAssignmentResponse,
  DataGroupApiAddItemsRequest,
  DataGroupApiCreateDataGroupRequest,
  DataGroupApiDeleteDataGroupRequest,
  DataGroupApiGetDataGroupRequest,
  DataGroupApiGetClaimedResourcesRequest,
  DataGroupApiGetDataAccessGroupsRequest,
  DataGroupApiGetIdentitiesUsingDataRoleRequest,
  DataGroupApiGetJitAccessGroupsRequest,
  DataGroupApiListAccessAssignmentsRequest,
  DataGroupApiListDataGroupsRequest,
  DataGroupApiListPolicyRolesRequest,
  DataGroupApiReplaceOnDemandAccessRequest,
  DataGroupApiReplacePermanentGroupAccessRequest,
  DataGroupApiReplacePermanentUserAccessRequest,
  DataGroupApiUpdateAccessAssignmentsRequest,
  DataGroupApiUpdateDataGroupRequest,
  DataGroupItemResponse,
  DataGroupPolicyRoleResponse,
  DataGroupResponse,
  IdentityInfo,
  JITAccessGroupInfo,
  PageDataGroupListResponse,
  PageCloudAccountResponse,
} from '@ariksa/abac/api';
import {
  DataAccessApiGetAuthorizedIdentitiesRequest,
  DataAccessApiGetDataAccessRolesRequest,
  DataAccessIdentityResponse,
  InsightV2ApiInsightV2AllRequest,
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
} from '@ariksa/inventory-core/api';
import { JitDataGroupRequestCountsResponse } from '@ariksa/jit/api';
import { QueryAction, QueryStatus } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';

import {
  GetDataGroupsSensitivityAction,
  updateDataGroupSensitivityStates,
} from 'containers/Jit/Components/dataGroupSensitivityUtils';

import { createSlice } from '../../../../utils/@reduxjs/toolkit';

import { ContainerState } from './types';

// The initial state of the PolicyHub container
export const initialState: ContainerState = {
  cloudAccounts: QueryState.init([]),
  oktaUsers: QueryState.init({} as InsightV2Pagination),
  claimedResources: QueryState.init([]),
  resources: {},
  dataGroups: PagedQueryState.init([]),
  dataGroupSensitivity: {},
  dataGroupRequestCounts: QueryState.init(
    {} as JitDataGroupRequestCountsResponse,
  ),
  dataGroupAction: QueryState.init({}),
  dataGroup: QueryState.init({} as DataGroupResponse),
  entitiesWithAccessToDataGroup: QueryState.init([]),
  identitiesUsingDataRole: QueryState.init([]),
  dataGroupDrawerItems: QueryState.init([]),
  authorizedGroups: QueryState.init([]),
  configure: {
    authorizedIdentities: QueryState.init([]),
    oktaGroups: QueryState.init([]),
    roles: QueryState.init([]),
    users: QueryState.init([]),
    saveConfiguration: QueryState.init({}),
    configuration: QueryState.init([]),
    replaceOnDemandAccess: QueryState.init([]),
    replacePermanentUserAccess: QueryState.init([]),
    replacePermanentGroupAccess: QueryState.init([]),
  },
  dataGroupRoles: {},
};

const dataGroupsSlice = createSlice({
  name: 'dataGroups',
  initialState,
  reducers: {
    getCloudAccounts(
      state,
      action: QueryAction<
        PageCloudAccountResponse,
        CloudAccountApiListAbacAccountsRequest
      >,
    ) {
      state.cloudAccounts = QueryState.next(state.cloudAccounts, action, {
        mapData: response => response?.items ?? [],
      });
    },

    getOktaUsers(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2AllRequest>,
    ) {
      state.oktaUsers = QueryState.next(state.oktaUsers, action);
    },

    getIdentitiesUsingDataRole(
      state,
      action: QueryAction<
        IdentityInfo[],
        DataGroupApiGetIdentitiesUsingDataRoleRequest
      >,
    ) {
      state.identitiesUsingDataRole = QueryState.next(
        state.identitiesUsingDataRole,
        action,
      );
    },

    getDataGroups(
      state,
      action: QueryAction<
        PageDataGroupListResponse,
        DataGroupApiListDataGroupsRequest
      >,
    ) {
      state.dataGroups = PagedQueryState.next(state.dataGroups, action, {
        mapData: r => r.items ?? [],
        mapTotalCount: r => r.total ?? 0,
      });
    },

    // Stores one bulk sensitivity response per data group id; a per-row
    // request here is an N+1 (Sentry ARIKSA-UI-ND).
    getDataGroupsSensitivity(state, action: GetDataGroupsSensitivityAction) {
      updateDataGroupSensitivityStates(state.dataGroupSensitivity, action);
    },

    getDataGroupRequestCounts(
      state,
      action: QueryAction<JitDataGroupRequestCountsResponse>,
    ) {
      state.dataGroupRequestCounts = QueryState.next(
        state.dataGroupRequestCounts,
        action,
      );
    },

    getClaimedResources(
      state,
      action: QueryAction<
        ClaimedDatabaseResource[],
        DataGroupApiGetClaimedResourcesRequest
      >,
    ) {
      state.claimedResources = QueryState.next(state.claimedResources, action);
    },

    getResources(
      state,
      action: QueryAction<
        Record<string, any>[],
        InsightV2ApiInsightV2AllRequest
      >,
    ) {
      const resourceType = action.payload.q.insightV2RequestBase.resource_type;
      if (!state.resources[resourceType!]) {
        state.resources[resourceType!] = QueryState.init([] as any[], {
          status: QueryStatus.pending,
        });
        return;
      }

      state.resources[resourceType!] = QueryState.next(
        state.resources[resourceType!],
        action,
      );
    },

    createDataGroup(
      state,
      action: QueryAction<
        DataGroupResponse,
        DataGroupApiCreateDataGroupRequest
      >,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },

    deleteDataGroup(
      state,
      action: QueryAction<
        DataGroupResponse,
        DataGroupApiDeleteDataGroupRequest
      >,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },

    updateDataGroup(
      state,
      action: QueryAction<
        DataGroupResponse,
        DataGroupApiUpdateDataGroupRequest
      >,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },

    addDataGroupItems(
      state,
      action: QueryAction<DataGroupItemResponse[], DataGroupApiAddItemsRequest>,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },

    getDataGroup(
      state,
      action: QueryAction<DataGroupResponse, DataGroupApiGetDataGroupRequest>,
    ) {
      state.dataGroup = QueryState.next(state.dataGroup, action);
    },

    getCloudRolesWithMasksForADataGroup(
      state,
      action: QueryAction<
        DataGroupPolicyRoleResponse[],
        DataGroupApiListPolicyRolesRequest
      >,
    ) {
      state.dataGroupDrawerItems = QueryState.next(
        state.dataGroupDrawerItems,
        action,
      );
    },

    getAuthorizedIdentities(
      state,
      action: QueryAction<
        DataAccessIdentityResponse[],
        DataAccessApiGetAuthorizedIdentitiesRequest
      >,
    ) {
      state.configure.authorizedIdentities = QueryState.next(
        state.configure.authorizedIdentities,
        action,
      );
    },

    getOktaGroups(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2AllRequest>,
    ) {
      state.configure.oktaGroups = QueryState.next(
        state.configure.oktaGroups,
        action,
        {
          mapData: response =>
            (response?.items ?? []).map(group => ({
              label: group?.name ?? group?.resource_id ?? group?.uuid,
              value: group?.uuid ?? group?.resource_id ?? group?.name,
              data: group,
            })),
        },
      );
    },

    getRoles(
      state,
      action: QueryAction<
        DataAccessIdentityResponse[],
        DataAccessApiGetDataAccessRolesRequest
      >,
    ) {
      state.configure.roles = QueryState.next(state.configure.roles, action);
    },

    getUsers(
      state,
      action: QueryAction<
        DataAccessIdentityResponse[],
        DataAccessApiGetAuthorizedIdentitiesRequest
      >,
    ) {
      state.configure.users = QueryState.next(state.configure.users, action);
    },

    saveConfiguration(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiUpdateAccessAssignmentsRequest
      >,
    ) {
      state.configure.saveConfiguration = QueryState.next(
        state.configure.saveConfiguration,
        action,
      );
    },

    getConfiguration(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiListAccessAssignmentsRequest
      >,
    ) {
      state.configure.configuration = QueryState.next(
        state.configure.configuration,
        action,
      );
    },

    replaceOnDemandAccess(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiReplaceOnDemandAccessRequest
      >,
    ) {
      state.configure.replaceOnDemandAccess = QueryState.next(
        state.configure.replaceOnDemandAccess,
        action,
      );
    },

    replacePermanentUserAccess(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiReplacePermanentUserAccessRequest
      >,
    ) {
      state.configure.replacePermanentUserAccess = QueryState.next(
        state.configure.replacePermanentUserAccess,
        action,
      );
    },

    replacePermanentGroupAccess(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiReplacePermanentGroupAccessRequest
      >,
    ) {
      state.configure.replacePermanentGroupAccess = QueryState.next(
        state.configure.replacePermanentGroupAccess,
        action,
      );
    },

    getResourceDetails(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2Request>,
    ) {
      state.entitiesWithAccessToDataGroup = QueryState.next(
        state.entitiesWithAccessToDataGroup,
        action,
        {
          mapData: r => r.items ?? [],
        },
      );
    },

    getDataAccessRoles(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiListAccessAssignmentsRequest
      >,
    ) {
      state.dataGroupDrawerItems = QueryState.next(
        state.dataGroupDrawerItems,
        action,
      );
    },

    getDataGroupRoles(
      state,
      action: QueryAction<
        DataAccessGroupInfo[],
        DataGroupApiGetDataAccessGroupsRequest
      >,
    ) {
      const dataGroupId = action.payload.q.dataGroupId;
      if (!state.dataGroupRoles[dataGroupId]) {
        state.dataGroupRoles[dataGroupId] = QueryState.init(
          [] as DataAccessGroupInfo[],
          {
            status: QueryStatus.pending,
          },
        );
        return;
      } else {
        state.dataGroupRoles[dataGroupId] = QueryState.next(
          state.dataGroupRoles[dataGroupId],
          action,
        );
      }
    },

    getJitEnabledOktaGroups(
      state,
      action: QueryAction<
        JITAccessGroupInfo[],
        DataGroupApiGetJitAccessGroupsRequest
      >,
    ) {
      state.dataGroupDrawerItems = QueryState.next(
        state.dataGroupDrawerItems,
        action,
      );
    },

    getAuthorizedGroups(
      state,
      action: QueryAction<
        JITAccessGroupInfo[],
        DataGroupApiGetJitAccessGroupsRequest
      >,
    ) {
      state.authorizedGroups = QueryState.next(state.authorizedGroups, action);
    },
  },
});

export const { actions, reducer, name: sliceKey } = dataGroupsSlice;
